import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import {
  addCustomRow,
  getPriceRows,
  updateCustomRow,
  updateCustomRowValue,
  updatePriceFieldOrder,
} from '../../../services/priceRowsService';
import { EnteredValue, NewPriceFieldRow, OrderedRow } from '../../../models/priceRows.model';
import { enteredValuesOf, valueKey } from '../../../util/bidTotal';
import { CellValue } from '../customRowValueCell/cellInput';

export const usePriceRows = (projectSlug: string, areaSlug: string, versionData: unknown) => {
  const [t] = useTranslation('projects', { keyPrefix: 'singlePurchase.bidOverview' });
  const [rows, setRows] = useState<OrderedRow[]>();
  const [enteredValues, setEnteredValues] = useState<Record<string, EnteredValue>>({});

  // versionData is a dependency because the server takes the price rows from the versions.
  useEffect(() => {
    let isCurrent = true;
    getPriceRows(projectSlug, areaSlug)
      .then(priceRows => {
        if (!isCurrent) return;
        setRows(priceRows.rows);
        setEnteredValues(enteredValuesOf(priceRows.values));
      })
      .catch(() => {
        if (isCurrent)
          NotificationManager.error(t('customRow.loadFailed', 'Raderna kunde inte hämtas'));
      });
    return () => {
      isCurrent = false;
    };
  }, [projectSlug, areaSlug, versionData, t]);

  const reorder = async (order: string[]) => {
    if (!rows) return;
    const previousRows = rows;
    const rowById = new Map(rows.map(row => [row.id, row]));
    const reordered = order.flatMap(id => rowById.get(id) ?? []);
    setRows(reordered);
    try {
      await updatePriceFieldOrder(projectSlug, areaSlug, order);
    } catch {
      setRows(current => (current === reordered ? previousRows : current));
      NotificationManager.error(t('priceFieldOrder.saveFailed', 'Ordningen kunde inte sparas'));
    }
  };

  const saveRow = async (row: NewPriceFieldRow, editedRowId?: string) => {
    try {
      setRows(
        editedRowId
          ? await updateCustomRow(projectSlug, areaSlug, editedRowId, row)
          : await addCustomRow(projectSlug, areaSlug, row),
      );
      return true;
    } catch {
      NotificationManager.error(t('customRow.saveFailed', 'Raden kunde inte sparas'));
      return false;
    }
  };

  const saveValue = async (
    customRowId: string,
    supplierId: string,
    refVersion: number,
    value: CellValue,
  ) => {
    const key = valueKey(customRowId, supplierId, refVersion);
    const previous = enteredValues[key];
    const entered: EnteredValue = typeof value === 'string' ? { text: value } : { value };
    setEnteredValues(current => ({ ...current, [key]: entered }));
    try {
      await updateCustomRowValue(projectSlug, areaSlug, customRowId, {
        supplierId,
        refVersion,
        value,
      });
    } catch {
      setEnteredValues(current =>
        current[key] === entered ? { ...current, [key]: previous } : current,
      );
      NotificationManager.error(t('customRow.saveFailed', 'Raden kunde inte sparas'));
    }
  };

  return { rows, enteredValues, reorder, saveRow, saveValue };
};
